// Marketing attribution captured on the LANDING url — last-touch.
//
// On every page load we read the campaign parameters out of the URL. If any are
// present we overwrite the stored set (last-touch wins) and remember when. At
// checkout the stored set is sent so the order can be attributed server-side, and
// so the server can decide which ad network — if any — the purchase conversion
// belongs to (api/internal/domain/tracking/adsource.go owns that decision; this
// file only reports what the URL said).
//
// Why STORED rather than read at checkout: by the time the buyer reaches
// /checkout the URL that carried `?fbclid=…` is several navigations in the past.
// Reading it there — which is what lib/matchsignals.ts used to do for fbclid and
// ttclid — finds a click id only when the buyer paid from the landing page itself.
//
// Zero main-bundle cost beyond this module; no third-party analytics SDK.

export interface UTM {
  utm_source: string
  utm_medium: string
  utm_campaign: string
  utm_term: string
  utm_content: string
}

/** Everything else the landing URL said, sent alongside UTM at checkout. */
export interface MarketingContext {
  utm_id: string
  utm_source_platform: string
  utm_creative_format: string
  utm_marketing_tactic: string
  /** Click parameter name → value, verbatim. Empty when the visit was not a click. */
  click_ids: Record<string, string>
  /** document.referrer of the landing page, for the seller — never for routing. */
  referrer: string
}

// HOST-SCOPED, and computed per call rather than at module load — the lazy form
// lib/klaviyokx.ts and lib/visitor.ts already use, because a module-level const
// would bake in whatever host was current when the chunk first loaded and that is
// the wrong value for anything resolving the store per tab (dev's
// `?preview_store=`).
//
// It was a bare `dk_attribution` until the click ids moved in here. Leaking a
// campaign string across two stores on one origin was untidy; leaking a click id
// is not, because the server resolves the order's ad_source from it — store B
// would route its conversion to the network that drove a visit to store A.
const key = () => `dk:attribution:${typeof location !== "undefined" ? location.host : ""}`
const MAX_LEN = 256
// Drop attribution older than 30 days so a long-dormant tab doesn't credit a
// stale campaign for a fresh purchase.
const TTL_MS = 30 * 24 * 60 * 60 * 1000

const UTM_FIELDS: (keyof UTM)[] = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"]

// The GA4 utm set beyond the five above. Split from them because the five are
// their own columns on the order and these ride in a JSONB blob — see migration
// 00121 for why that split is where it is.
const UTM_EXTRA = ["utm_id", "utm_source_platform", "utm_creative_format", "utm_marketing_tactic"] as const

// Every ad network's click id, LOWER-CASE. This list must stay equal to
// ClickIDParams in api/internal/domain/tracking/adsource.go: a name only this side
// knows is stored and then resolves to no network, and one only that side knows is
// never captured at all.
//
// Deliberately absent: `gad_source`, `gclsrc`, `gad_campaignid`. Google appends
// those to plenty of links that are not ad clicks, so storing them as click ids
// would spend one of the ten slots below on something that proves nothing.
const CLICK_IDS = [
  "fbclid", "gclid", "gbraid", "wbraid", "ttclid", "epik",
  "msclkid", "sccid", "rdt_cid", "twclid", "li_fat_id",
]

interface Stored extends UTM, MarketingContext {
  ts?: number
}

const empty = (): Stored => ({
  utm_source: "", utm_medium: "", utm_campaign: "", utm_term: "", utm_content: "",
  utm_id: "", utm_source_platform: "", utm_creative_format: "", utm_marketing_tactic: "",
  click_ids: {},
  referrer: "",
})

/**
 * Reads the query string into a lower-cased map.
 *
 * Case-folded because the networks are not consistent: Snapchat ships `ScCid`, and
 * a param typed by hand into an agency's link can arrive in any casing. The server
 * lower-cases these names too, so the two agree on what `ScCid` is called.
 */
function params(): Record<string, string> {
  const out: Record<string, string> = {}
  try {
    for (const [k, v] of new URLSearchParams(window.location.search)) {
      const name = k.trim().toLowerCase()
      if (name && !(name in out)) out[name] = v.trim().slice(0, MAX_LEN)
    }
  } catch {
    /* malformed query string — attribution is best-effort */
  }
  return out
}

// captureAttribution reads the campaign parameters from the current URL and, if
// any are present, persists them as the new last-touch. Call once on app boot,
// before the router can strip the query. Safe to call repeatedly.
export function captureAttribution(): void {
  try {
    const p = params()
    const next = empty()
    let found = false
    for (const f of UTM_FIELDS) {
      if (p[f]) {
        next[f] = p[f]!
        found = true
      }
    }
    for (const f of UTM_EXTRA) {
      if (p[f]) {
        next[f] = p[f]!
        found = true
      }
    }
    // A click id alone is enough to overwrite the stored touch, and that is the
    // point: `?gclid=…` with no utm tags at all is an ordinary Google Ads click on
    // a link nobody tagged, and the old rule (any utm_* present) captured nothing
    // for it — so the order looked like direct traffic and the conversion went to
    // every pixel the store had.
    for (const name of CLICK_IDS) {
      if (p[name]) {
        next.click_ids[name] = p[name]!
        found = true
      }
    }
    if (!found) return
    try {
      next.referrer = (document.referrer || "").slice(0, MAX_LEN)
    } catch {
      /* no referrer available */
    }
    localStorage.setItem(key(), JSON.stringify({ ...next, ts: Date.now() }))
  } catch {
    // private mode / disabled storage — attribution is best-effort.
  }
}

/** The stored last-touch, or an all-empty record when there is none / it is stale. */
function stored(): Stored {
  try {
    const raw = localStorage.getItem(key())
    if (!raw) return empty()
    const parsed = JSON.parse(raw) as Partial<Stored>
    if (parsed.ts && Date.now() - parsed.ts > TTL_MS) {
      localStorage.removeItem(key())
      return empty()
    }
    const out = empty()
    for (const f of UTM_FIELDS) if (typeof parsed[f] === "string") out[f] = parsed[f]
    for (const f of UTM_EXTRA) if (typeof parsed[f] === "string") out[f] = parsed[f]
    if (typeof parsed.referrer === "string") out.referrer = parsed.referrer
    const ids = parsed.click_ids
    if (ids && typeof ids === "object") {
      for (const name of CLICK_IDS) {
        const v = (ids as Record<string, unknown>)[name]
        if (typeof v === "string" && v) out.click_ids[name] = v
      }
    }
    return out
  } catch {
    return empty()
  }
}

// getAttribution returns the stored last-touch UTM (all-empty when none / stale).
export function getAttribution(): UTM {
  const s = stored()
  return {
    utm_source: s.utm_source, utm_medium: s.utm_medium, utm_campaign: s.utm_campaign,
    utm_term: s.utm_term, utm_content: s.utm_content,
  }
}

// getMarketing returns the rest of the stored last-touch: the GA4 utm extras, the
// click ids and the landing referrer. Sent as its own `marketing` object at
// checkout rather than folded into the UTM one, because the server stores the two
// in different shapes (five columns vs one JSONB) for different reasons.
export function getMarketing(): MarketingContext {
  const s = stored()
  return {
    utm_id: s.utm_id, utm_source_platform: s.utm_source_platform,
    utm_creative_format: s.utm_creative_format, utm_marketing_tactic: s.utm_marketing_tactic,
    click_ids: s.click_ids,
    referrer: s.referrer,
  }
}

/** The stored click ids alone — what lib/matchsignals.ts needs for `_fbc`. */
export function getClickIDs(): Record<string, string> {
  return stored().click_ids
}

// hasAttribution reports whether any UTM field is set.
export function hasAttribution(u: UTM): boolean {
  return UTM_FIELDS.some(f => u[f] !== "")
}
